import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../../store';
import { IAssets, IAccount, IAccountStorage } from '../../app/types';

const initialState = {
  accounts: [] as IAccount[],
  trash: {
    assetItems: [] as IAssets[],
  },
  status: 'inactive',
  error: null,
};

const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    // Add multiple accounts (initialize)
    initAccounts: (state, action: PayloadAction<any>) => {
      // Receiver account array
      const acctArray = action.payload;
      // Map payload by adding needed properties to new accounts
      const payload = acctArray.map((acct: any) => {
        // Check first for existing account
        const existAcct = state.accounts.find((stateAcct) => acct.address === stateAcct.address);
        if (existAcct)
          return {
            ...existAcct,
          };
        // Otherwise add needed properties to new account
        return {
          ...acct,
          assetItems: [],
          initialAssets: [],
          balance: 0,
          minBalance: 0,
        };
      });
      // Overwrite state.accounts with the new payload
      state.accounts = payload;
    },
    addAccounts: (state, action: PayloadAction<IAccountStorage[]>) => {
      const newAccts = action.payload;
      newAccts.forEach((newAcct) => {
        const existAcct = state.accounts.find((stateAcct) => newAcct.address === stateAcct.address);
        // Some logic to not have duplicate accounts
        if (existAcct) return;
        // Add missing properties
        const addAcct = {
          ...newAcct,
          assetItems: [],
          initialAssets: [],
          balance: 0,
          minBalance: 0,
        };
        // Check if first account added, otherwise push it
        if (!state.accounts?.length || !state.accounts[0].address.length) {
          state.accounts[0] = addAcct;
        } else {
          state.accounts.push(addAcct);
        }
      });
    },
    addAcctBalances: (
      state,
      action: PayloadAction<{
        address: string;
        balance: number;
        minBalance: number;
      }>
    ) => {
      // Populate balances from API and derived values
      const { address, balance, minBalance } = action.payload;
      const existAccount = state.accounts.find((acct: any) => acct.address === address);
      if (!existAccount) return;
      existAccount.balance = balance;
      existAccount.minBalance = minBalance;
    },
    initAcctAssetItems: (state, action: PayloadAction<IAssets[]>) => {
      if (!action.payload.length) return;
      const initAssets = action.payload;
      // Find account by sender using first asset item as they're all from same sender
      const { sender } = initAssets[0];
      const account = state.accounts.find((acct) => acct.address === sender);
      if (!account) return;
      // When repopulating asset items, make sure it doesnt already exist in trash
      account.assetItems = initAssets.filter((asset) => {
        return !state.trash.assetItems.some((trashItem) => asset.id === trashItem.id);
      });

      account.initialAssets = initAssets;
    },
    toggleActiveAccount: (state, action: PayloadAction<{ address: string }>) => {
      const account = state.accounts.find((accts: any) => accts.address === action.payload.address);
      if (!account) return;
      account.active = !account.active;
    },
    setAllAccountsActive: (state) => {
      // Set active flag to true for all accounts that meet minimum balance
      state.accounts.forEach((acct: any) => {
        if (acct.balance >= 100000) {
          acct.active = true;
        }
      });
      state.status = 'active';
    },
    clearAllAccountsActive: (state) => {
      // Clear all active flags
      state.accounts.forEach((acct: any) => {
        acct.active = false;
      });
      state.status = 'inactive';
    },
    setAssetItems: (state, action: PayloadAction<IAssets[]>) => {
      if (!action.payload) return;
      const acctAssetItems = action.payload;

      state.accounts?.forEach((account: any, ind: number) => {
        account.assetItems = acctAssetItems[ind];
      });
    },
    setTrashAssetItems: (state, action: PayloadAction<IAssets[]>) => {
      const assetItems = action.payload;
      state.trash.assetItems = assetItems;
    },
    addOptInAssetItem: (state, action: PayloadAction<IAssets>) => {
      const addAssetItem = action.payload;
      // Grab receiver account from state
      const receiverAccount = state.accounts.find((acct) => acct.address === addAssetItem.receiver);
      // Check if receiver has the asset already
      if (receiverAccount) {
        const foundAsset = receiverAccount.initialAssets.find(
          (el: IAssets) => el.assetIndex === addAssetItem.assetIndex
        );
        if (foundAsset !== undefined) return state;
        // If receiver does not have asset, add it
        receiverAccount.assetItems.push(addAssetItem);
      }
    },
    addTrashAssetItem: (state, action: PayloadAction<IAssets>) => {
      const addTrashAssetItem = action.payload;
      state.trash.assetItems.push(addTrashAssetItem);
    },
    resetAssetsByAccount: (state, action: PayloadAction<{ id: string }>) => {
      const address = action.payload.id;
      // Remove trash assets from account
      state.trash.assetItems = state.trash.assetItems.filter((item) => item.sender !== address);

      // Remove assetItems from acct being deactivated
      state.accounts = state.accounts.map((acct) => {
        const newAssetItems = acct.assetItems.filter((item: any) => {
          return item.sender !== address;
        });
        return { ...acct, assetItems: newAssetItems };
      });

      const acct = state.accounts.find((acct) => acct.address === address);
      // Put assetitems back that were sent to acct being deactivated
      acct?.assetItems.forEach((item) => {
        if (item.sender !== address) {
          const receivingAcct = state.accounts.find((acct) => acct.address === item.sender);
          receivingAcct?.assetItems.push(item);
        }
      });
    },
    removeTrashItemByAccount: (state, action: PayloadAction<any>) => {
      const address = action.payload.id;
      state.trash.assetItems = state.trash.assetItems.filter((item) => item.sender !== address);
    },
    setAccountStatus: (state, action) => {
      const { status } = action.payload;
      state.status = status;
    },
    resetAssets: (state) => {
      state.accounts?.map((acct: any) => (acct.assetItems = acct.initialAssets));
      state.trash.assetItems = [];
    },
    deleteAccount: (state, action: PayloadAction<{ address: string }>) => {
      const { address } = action.payload;
      const storageAccounts = JSON.parse(localStorage.getItem('storageAccounts') || '[]').filter(
        (acct: any) => acct.address !== address
      );
      localStorage.setItem('storageAccounts', JSON.stringify(storageAccounts));

      const newAccounts = state.accounts?.filter((acct) => {
        return acct.address !== address;
      });
      state.accounts = newAccounts;
    },
    // deleteWebAccount: (state, action: PayloadAction<{ address: string }>) => {
    //   const { address } = action.payload;
    //   state.accounts = state.accounts.filter((acct) => acct.address !== address);
    //   const webAccounts = JSON.parse(localStorage.getItem('webAccounts') || '[]').filter(
    //     (acct: any) => acct.address !== address
    //   );
    //   localStorage.setItem('webAccounts', JSON.stringify(webAccounts));
    // },
    deleteLocalAccounts: (state) => {
      state.accounts = state.accounts.filter((acct) => {
        return acct.source !== 'Purrser';
      });
    },
    setName: (state, action: PayloadAction<any>) => {
      const account = state.accounts.find((accts: any) => accts.address === action.payload.address);
      if (!account || !action.payload.name) return;
      account.name = action.payload.name;
    },
    changeAccountType: (
      state,
      action: PayloadAction<{
        address: string;
        type: 'MyAlgo' | 'AlgoSigner' | 'Purrser' | 'walletConnect';
        name: string;
      }>
    ) => {
      const account = state.accounts.find((acct) => acct.address === action.payload.address);
      if (!account) return;
      account.source = action.payload.type;
      account.name = action.payload.name;
    },
    changeAccountOrder: (state, action: PayloadAction<{ startIndex: any; endIndex: any }>) => {
      const { startIndex, endIndex } = action.payload;
      const acctList = state.accounts;
      const firstItem = acctList[startIndex];
      state.accounts[startIndex] = acctList[endIndex];
      state.accounts[endIndex] = firstItem;
    },
    transferWallet: (state, action: PayloadAction<{ sender: string; receiver: string }>) => {
      const { sender, receiver } = action.payload;
      const senderAccount = state.accounts.find((account) => account.address === sender);
      const receivingAccount = state.accounts.find((account) => account.address === receiver);
      if (receivingAccount && senderAccount) {
        let newAssetItems = senderAccount.assetItems.map((asset) => {
          return { ...asset, receiver: receiver };
        });
        receivingAccount.assetItems = [...receivingAccount.assetItems, ...newAssetItems];
      }
      if (senderAccount) senderAccount.assetItems = [];
    },
    transferWalletAssets: (state, action: PayloadAction<{ sender: string; receiver: string }>) => {
      const { sender, receiver } = action.payload;
      const senderAccount = state.accounts.find((account) => account.address === sender);
      const receivingAccount = state.accounts.find((account) => account.address === receiver);
      // NOT WORKING
      if (receivingAccount && senderAccount) {
        let newAssetItems = senderAccount.assetItems
          .filter((assetItem) => assetItem.assetIndex !== 0)
          .map((asset) => {
            return { ...asset, receiver: receiver };
          });
        receivingAccount.assetItems = [...receivingAccount.assetItems, ...newAssetItems];
      }
      if (senderAccount)
        senderAccount.assetItems = senderAccount.assetItems.filter(
          (assetItem) => assetItem.assetIndex === 0
        );
    },
  },
});

export const {
  initAccounts,
  changeAccountOrder,
  addAccounts,
  setName,
  addAcctBalances,
  toggleActiveAccount,
  initAcctAssetItems,
  setAssetItems,
  resetAssetsByAccount,
  setTrashAssetItems,
  setAllAccountsActive,
  clearAllAccountsActive,
  addTrashAssetItem,
  removeTrashItemByAccount,
  resetAssets,
  deleteAccount,
  deleteLocalAccounts,
  // deleteWebAccount,
  addOptInAssetItem,
  changeAccountType,
  setAccountStatus,
  transferWallet,
  transferWalletAssets,
} = accountsSlice.actions;

export default accountsSlice.reducer;

export const selectAllAccounts = (state: RootState) => state.accounts && state.accounts.accounts;

export const getAccountsLength = (state: RootState) =>
  state.accounts && state.accounts.accounts.length;

export const selectAccountByAddress = (state: RootState, address: string) => {
  return state.accounts.accounts.find((acct: any) => acct.address === address);
};

export const selectActiveAccounts = (state: RootState) =>
  state.accounts && state.accounts.accounts.filter((acct: any) => acct.active !== false);

export const selectAssetsByAccount = (state: RootState, sender: string) => {
  const acct = state.accounts.accounts.find((acct) => acct.address === sender);
  return acct && acct.assetItems;
};

export const selectMyAlgoAccounts = (state: RootState) => {
  return state.accounts.accounts.filter((acct) => acct.source === 'MyAlgo');
};

export const selectAlgoSignerAccounts = (state: RootState) => {
  return state.accounts.accounts.length
    ? state.accounts.accounts.filter((acct) => {
        return acct?.source === 'AlgoSigner';
      })
    : [];
};

export const selectWebAccounts = (state: RootState) => {
  return state.accounts.accounts.length
    ? state.accounts.accounts.filter((acct) => {
        return acct?.source !== 'Purrser';
      })
    : [];
};
export const selectLocalAccounts = (state: RootState): IAccount[] => {
  return state.accounts.accounts.length
    ? state.accounts.accounts.filter((acct) => {
        return acct?.source === 'Purrser';
      })
    : [];
};
export const selectWcAccounts = (state: RootState): IAccount[] => {
  return state.accounts.accounts.length
    ? state.accounts.accounts.filter((acct) => {
        return acct?.source === 'walletConnect';
      })
    : [];
};

export const getMyAlgoAccountsLength = (state: RootState) => {
  return state.accounts.accounts.filter((acct) => acct.source === 'MyAlgo').length;
};
export const getAlgoSignerAccountsLength = (state: RootState) => {
  return state.accounts.accounts.length
    ? state.accounts.accounts.filter((acct) => {
        return acct?.source === 'AlgoSigner';
      }).length
    : 0;
};
export const getLocalAccountsLength = (state: RootState) => {
  return state.accounts.accounts.length
    ? state.accounts.accounts.filter((acct) => {
        return acct?.source === 'Purrser';
      }).length
    : 0;
};

export const getNumActive = (state: RootState) => {
  if (state.accounts.accounts) {
    return state.accounts.accounts.reduce((acc, cur) => {
      const a = cur.active ? 1 : 0;
      return acc + a;
    }, 0);
  } else {
    return 0;
  }
};

export const getHasValidAccount = (state: RootState) => {
  let hasValid = false;
  state.accounts.accounts &&
    state.accounts.accounts.forEach((acct) => {
      if (acct.balance > 0.1) {
        hasValid = true;
      } else return;
    });
  return hasValid;
};

export const getIsMissingActive = (state: RootState) => {
  let missingActive = false;
  state.accounts.accounts &&
    state.accounts.accounts.forEach((acct) => {
      if (acct.balance > 0.1 && acct.active === false) {
        missingActive = true;
      } else return;
    });
  return missingActive;
};
